import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../services/api";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const monthLabel = (yyyyMm) => {
  const [year, month] = yyyyMm.split("-");
  return `${MONTH_NAMES[Number(month) - 1]}-${year.slice(2)}`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  return `${Number(day)}-${MONTH_NAMES[Number(month) - 1]}-${year.slice(2)}`;
};

const money = (value) => Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pickUnit = (values) => {
  const max = Math.max(0, ...values);
  if (max >= 10000000) return { label: "In Crores", divisor: 10000000 };
  if (max >= 100000) return { label: "In Lakhs", divisor: 100000 };
  return { label: "In Rupees", divisor: 1 };
};

const TILE_HEIGHT = 372;

const TrendTile = ({ title, subtitle, data, color }) => {
  const unit = pickUnit(data.map((d) => Number(d.amount)));
  const chartData = data.map((d) => ({ month: monthLabel(d.month), value: Number(d.amount) / unit.divisor, raw: Number(d.amount) }));

  return (
    <div className="content-card" style={{ height: TILE_HEIGHT }}>
      <h3 style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>{title}</h3>
      <div style={{ fontSize: "12px", color: "var(--color-text-muted)", marginBottom: "10px" }}>{subtitle}</div>
      <div style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>{unit.label}</div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} angle={-40} textAnchor="end" height={44} />
            <YAxis tick={{ fontSize: 10 }} width={48} />
            <Tooltip formatter={(_value, _name, item) => [money(item.payload.raw), "Nett Transactions"]} />
            <Line type="linear" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: "11px", color: "var(--color-text-muted)", textAlign: "center" }}>
        <span style={{ display: "inline-block", width: 10, height: 10, background: color, marginRight: 6 }} />
        Nett Transactions
      </div>
    </div>
  );
};

const Overview = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setOverview(await api.get("/api/v1/reports/overview"));
    } catch (err) {
      setError(err.message || "Failed to load overview");
      toast.error(err.message || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subtitle = overview ? `${formatDate(overview.fromDate)} to ${formatDate(new Date().toISOString().slice(0, 10))}` : "";

  return (
    <div style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay" style={{ position: "fixed" }}>
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading overview...</div>
          </div>
        </div>
      ) : null}

      {error && !overview ? (
        <div className="content-card">
          <h3 style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>Couldn't load the overview</h3>
          <p style={{ color: "var(--color-text-muted)", margin: "8px 0 12px" }}>
            {error}. If this says "status 403", your login has expired — click Logout and sign in again.
          </p>
          <button className="primary" onClick={load}>
            Retry
          </button>
        </div>
      ) : null}

      {overview ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "16px" }}>
            <TrendTile title="Sales Trend" subtitle={subtitle} data={overview.salesTrend} color="#4f46e5" />
            <TrendTile title="Purchase Trend" subtitle={subtitle} data={overview.purchaseTrend} color="#0891b2" />
            <div className="content-card" style={{ height: TILE_HEIGHT }}>
              <h3 style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>Inventory Details</h3>
              <div style={{ fontSize: "12px", color: "var(--color-text-muted)", marginBottom: "14px" }}>{subtitle}</div>
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Particulars</th>
                    <th style={{ textAlign: "right" }}>Quantity</th>
                    <th style={{ textAlign: "right" }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Closing Stock</td>
                    <td style={{ textAlign: "right" }}>{Number(overview.closingStockQuantity).toLocaleString("en-IN", { maximumFractionDigits: 3 })} Nos</td>
                    <td style={{ textAlign: "right" }}>{money(overview.closingStockValue)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default Overview;
